import type { DependencyList, RefObject } from 'react'

import { useEffect, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

export const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

let registered = false

export const registerGsap = (): void => {
    if (registered || typeof window === 'undefined') {
        return
    }

    gsap.registerPlugin(ScrollTrigger)

    gsap.defaults({
        ease: 'noma',
        duration: 1
    })

    ScrollTrigger.config({ ignoreMobileResize: true })

    registered = true
}

/* The reference leans on two easings: a very slow-in/fast-out curve for entrances
   and a softer expo-out for hovers. Registering them once keeps every section
   choreographed against the same rhythm instead of drifting apart. */
if (typeof window !== 'undefined') {
    gsap.registerEase('noma', (p: number): number => {
        return 1 - Math.pow(1 - p, 3.4)
    })

    gsap.registerEase('noma-soft', (p: number): number => {
        return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
    })
}

type ContextFn = (self: gsap.Context) => void | (() => void)

export const useGsap = (
    fn: ContextFn,
    scope?: RefObject<HTMLElement | null>,
    deps: DependencyList = []
): void => {
    useIsomorphicLayoutEffect(() => {
        registerGsap()

        const ctx = gsap.context(fn, scope?.current ?? undefined)

        return () => ctx.revert()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}

export const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined') {
        return false
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export { gsap, ScrollTrigger }
